import { configureStore } from '@reduxjs/toolkit';
import { isEqual, isObject } from 'lodash';
import { shallowEqual, useSelector } from 'react-redux';
import { combineReducers } from 'redux';
import rendererProviderReducer, { initialRendererState, RendererProviderAction, RendererProviderStore } from './rendererReducer';
import { actionCreator, isType } from './utils/actions';

export interface State {
  rendererProvider: RendererProviderStore;
}

export const initialStoreState: State = {
  rendererProvider: initialRendererState,
};

export const logoutUserAction = actionCreator<Record<string, never>>('LOGOUT_USER');
type LogoutUserAction = ReturnType<typeof logoutUserAction>;

export type Action = RendererProviderAction | LogoutUserAction;

export const ActionType = {};

const reducers = {
  rendererProvider: rendererProviderReducer,
};

const combinedReducers = combineReducers(reducers);

const rootReducer = (state: ReturnType<typeof combinedReducers> = initialStoreState, action: Action) => {
  if (isType(action, logoutUserAction)) {
    return combinedReducers(initialStoreState, action);
  }
  return combinedReducers(state, action);
};

const store = configureStore({ reducer: rootReducer, devTools: import.meta.env.NODE_ENV === 'development' });

export const useCustomSelector = <TState = State, TSelected = unknown>(selector: (state: TState) => TSelected, properties: (keyof TSelected)[] = [], listenToEverything = !properties.length) => {
  return useSelector(selector, (left: TSelected, right: TSelected) => {
    if (!left && !right) return true;
    const leftRecord = left as Record<string, unknown>;
    const rightRecord = right as Record<string, unknown>;
    if (listenToEverything) {
      if (!right && !!left) return false;
      if (!left && !!right) return false;
      for (const property of Object.keys(rightRecord).concat(Object.keys(leftRecord))) {
        const equal = shallowEqual(leftRecord[property], rightRecord[property]);
        if (!equal) return false;
      }
    }
    for (const property of properties) {
      const key = property as string;
      if (!leftRecord[key] && rightRecord[key]) return false;
      if (!rightRecord[key] && leftRecord[key]) return false;
      let equal = false;
      if (isObject(leftRecord[key]) || isObject(rightRecord[key])) equal = isEqual(leftRecord[key], rightRecord[key]);
      else equal = shallowEqual(leftRecord[key], rightRecord[key]);
      if (!equal) return false;
    }
    return true;
  });
};

export default store;
